"use client";

import Script from "next/script";
import { recordVisitorJourney, updateTrackingConsent } from "@/lib/analytics-runtime";
import { usePathname, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";

import {
  COOKIE_CONSENT_CHANGE_EVENT,
  hasCookieConsent,
} from "@/lib/cookie-consent";
import { getAnalyticsPublicConfig } from "@/lib/analytics-config";
import { installSiteTracking } from "@/lib/site-tracking";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

function useAnalyticsConsent(enabled: boolean, gaId: string | null): boolean {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const sync = () => {
      const allowed = hasCookieConsent("analytics");
      updateTrackingConsent(allowed, gaId);
      if (!allowed) { try { localStorage.removeItem("visitorJourney"); } catch { /* Storage unavailable. */ } }
      setAllowed(allowed);
    };
    sync();
    window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
    return () => window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, sync);
  }, [enabled, gaId]);

  return allowed;
}

function CcAnalyticsPageviews({
  gaMeasurementId,
  metaPixelId,
  active,
  gaReady,
  metaReady,
}: {
  gaMeasurementId: string | null;
  metaPixelId: string | null;
  active: boolean;
  gaReady: boolean;
  metaReady: boolean;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams?.toString() ?? "";
  const pagePath = search ? `${pathname}?${search}` : pathname;

  useEffect(() => {
    if (active) return installSiteTracking();
  }, [active, pagePath]);

  useEffect(() => {
    if (!active) return;

    recordVisitorJourney();
  }, [active, pagePath]);

  useEffect(() => {
    if (!active || !gaReady || !gaMeasurementId) return;
    window.gtag?.("event", "page_view", {
      send_to: gaMeasurementId,
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [active, gaReady, gaMeasurementId, pagePath]);

  useEffect(() => {
    if (!active || !metaReady || !metaPixelId) return;
    window.fbq?.("track", "PageView");
  }, [active, metaReady, metaPixelId, pagePath]);

  return null;
}

export function CcAnalytics() {
  const config = useMemo(() => getAnalyticsPublicConfig(), []);
  const consent = useAnalyticsConsent(config.enabled, config.gaMeasurementId);
  const active = config.enabled && consent;

  const [gtagReady, setGtagReady] = useState(false);
  const onGtagReady = useCallback(() => setGtagReady(true), []);
  const [metaReady, setMetaReady] = useState(false);
  const onMetaReady = useCallback(() => setMetaReady(true), []);

  if (!config.enabled) {
    return null;
  }

  const { gaMeasurementId, gtmId, metaPixelId, gaManagedByGtm } = config;

  return (
    <>
      {active && gtmId ? (
        <>
          <Script
            id="cc-gtm"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`,
            }}
          />
          <noscript>
            <iframe
              title="Google Tag Manager"
              src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            />
          </noscript>
        </>
      ) : null}

      {active && gaMeasurementId && !gaManagedByGtm ? (
        <>
          <Script
            id="cc-gtag-loader"
            src={`https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`}
            strategy="afterInteractive"
            onLoad={onGtagReady}
          />
          <Script
            id="cc-gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `window.dataLayer=window.dataLayer||[];gtag('js',new Date());gtag('config','${gaMeasurementId}',{send_page_view:false});`,
            }}
            onReady={onGtagReady}
          />
        </>
      ) : null}

      {active && metaPixelId ? (
        <>
          <Script
            id="cc-meta-pixel"
            onReady={onMetaReady}
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window,document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('consent','grant');fbq('init','${metaPixelId}');`,
            }}
          />
          <noscript>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              alt=""
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      ) : null}

      {active ? (
        <Suspense fallback={null}>
          <CcAnalyticsPageviews
            gaMeasurementId={gaManagedByGtm ? null : gaMeasurementId}
            metaPixelId={metaPixelId}
            active={active}
            gaReady={gtagReady}
            metaReady={metaReady}
          />
        </Suspense>
      ) : null}

    </>
  );
}
